import { useEffect, useId, useRef, useState } from 'react'
import {
  PHONE_COUNTRIES,
  applyPhoneMask,
  flagUrl,
  maskPlaceholder,
  type PhoneCountry,
} from '../lib/phoneCountries'

export type LoginMode = 'phone' | 'email'

type Props = {
  mode: LoginMode
  onModeChange: (mode: LoginMode) => void
  phone: string
  onPhoneChange: (masked: string) => void
  email: string
  onEmailChange: (value: string) => void
  country: PhoneCountry
  onCountryChange: (country: PhoneCountry) => void
}

export function LoginIdentifierField({
  mode,
  onModeChange,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  country,
  onCountryChange,
}: Props) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  return (
    <div className="hub-login-id">
      <div className="hub-login-tabs" role="tablist" aria-label="Tipo de acesso">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'phone'}
          className={`hub-login-tab${mode === 'phone' ? ' hub-login-tab--active' : ''}`}
          onClick={() => onModeChange('phone')}
        >
          Telefone
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'email'}
          className={`hub-login-tab${mode === 'email' ? ' hub-login-tab--active' : ''}`}
          onClick={() => onModeChange('email')}
        >
          E-mail
        </button>
      </div>

      {mode === 'phone' ? (
        <label className="bp-field">
          <span>Telefone</span>
          <div className="hub-phone-wrap" ref={wrapRef}>
            <button
              type="button"
              className="hub-phone-country"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              onClick={(e) => {
                e.stopPropagation()
                setOpen((v) => !v)
              }}
            >
              <img src={flagUrl(country.flag)} alt="" className="hub-phone-flag" width={20} height={15} />
              <span className="hub-phone-ddi">{country.code}</span>
              <span className="hub-phone-chevron" aria-hidden>
                ▾
              </span>
            </button>
            <input
              className="hub-phone-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              placeholder={maskPlaceholder(country.mask)}
              required={mode === 'phone'}
              onChange={(e) => onPhoneChange(applyPhoneMask(e.target.value, country.mask))}
            />
            {open ? (
              <div className="hub-phone-dropdown" id={listId} role="listbox">
                {PHONE_COUNTRIES.map((c) => {
                  const key = `${c.flag}-${c.code}`
                  const selected = c.flag === country.flag && c.code === country.code
                  return (
                    <button
                      key={key}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`hub-phone-option${selected ? ' hub-phone-option--selected' : ''}`}
                      onClick={() => {
                        onCountryChange(c)
                        if (phone) onPhoneChange(applyPhoneMask(phone, c.mask))
                        setOpen(false)
                      }}
                    >
                      <img src={flagUrl(c.flag)} alt="" className="hub-phone-flag" width={20} height={15} />
                      <span className="hub-phone-option__name">{c.name}</span>
                      <span className="hub-phone-option__code">{c.code}</span>
                    </button>
                  )
                })}
              </div>
            ) : null}
          </div>
        </label>
      ) : (
        <label className="bp-field">
          <span>E-mail</span>
          <input
            className="bp-input"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            placeholder="seu@email.com"
            required={mode === 'email'}
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </label>
      )}
    </div>
  )
}
